import React, { useEffect, useRef, useState } from 'react';
import { showNotification } from 'react-admin';
import QRCode from 'react-qr-code';
import { connect } from 'react-redux';
import config from '../config';
import { setOpenQrScanner, startCountdown, startPolling, stopCountdown, stopPolling } from './virtualStationActions';
import { Dialog, DialogContent, DialogTitle, IconButton, Button } from '@material-ui/core';
import { Typography } from '@material-ui/core/';
import CloseIcon from '@material-ui/icons/Close';

const env = process.env.REACT_APP_CUSTOM_NODE_ENV;
const BASE_URL = config[env].url;

const ShowQRButton = ({
    record,
    resource,
    permissions,
    startPolling,
    stopPolling,
    startCountdown,
    stopCountdown,
    setOpenQrScanner,
    showNotification,
    countdown,
    isPolling,
}) => {
    const [showQR, setShowQR] = useState(false);
    const [isConnected, setIsConnected] = useState(true);
    const pollingInterval = useRef(null);
    const countdownInterval = useRef(null);
    const abortControllerRef = useRef(null);

    const getApiBaseUrl = () => {
        return permissions === 'promoter'
            ? `${BASE_URL}promoter/`
            : `${BASE_URL}admin/`;
    };

    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            setIsConnected(true);
            // Resume polling if it was paused due to connection loss
            if (showQR && isPolling) {
                if (pollingInterval.current) {
                    clearInterval(pollingInterval.current);
                }
                pollingInterval.current = setInterval(() => {
                    if (isConnected) {
                        executePolling();
                    }
                }, 2000);
            }
        };
        const handleOffline = () => setIsConnected(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Check service worker for more detailed connection status
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', event => {
                if (event.data && event.data.type === 'CONNECTION_STATUS') {
                    setIsConnected(event.data.payload.connected);
                }
            });
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [showQR, isPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
            }
            if (countdownInterval.current) {
                clearInterval(countdownInterval.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Handle countdown timer
    useEffect(() => {
        if (countdown > 0 && showQR) {
            countdownInterval.current = setInterval(() => {
                startCountdown(countdown - 1);
            }, 1000);
        } else if (countdown === 0 && showQR) {
            handleCloseQR();
        }

        return () => {
            if (countdownInterval.current) {
                clearInterval(countdownInterval.current);
            }
        };
    }, [countdown, showQR]);

    const handleOpenQR = () => {
        if (!record || !record.sno) {
            showNotification('Station data not available', 'warning');
            return;
        }

        setShowQR(true);
        startCountdown(30); // 30 seconds countdown

        // Start polling only if connected
        if (isConnected) {
            startPolling();
            executePolling();

            pollingInterval.current = setInterval(() => {
                if (isConnected) {
                    executePolling();
                }
            }, 2000); // Poll every 2 seconds
        }
    };

    const handleCloseQR = () => {
        setShowQR(false);
        stopPolling();
        stopCountdown();

        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
        }

        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    const executePolling = async () => {
        if (!record || !record.sno || !resource) {
            return;
        }

        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        try {
            const apiUrl = getApiBaseUrl();
            const url = `${apiUrl}${resource}/${record.sno}/poll`;
            const token = localStorage.getItem('token');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                signal: abortControllerRef.current.signal,
            });

            const data = await response.json();

            // If we get a 200 status and data is not null, stop polling and close QR
            if (response.status === 200 && data.session) {
                handleCloseQR();
                showNotification(`Session waiting for device has been found`);
                // Dispatch action to open QR scanner in ScanDeviceButton
                setOpenQrScanner(true);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Polling error:', error);
            }
        }
    };

    return (
        <>
            <Button
                label="Show QR"
                onClick={handleOpenQR}
                disabled={!record || !record.station_qr}
                variant="contained"
                color={'primary'}
            >
                Show QR
            </Button>
            {showQR && record && (
                <Dialog open={showQR} maxWidth="md" onClose={handleCloseQR}>
                    <DialogTitle>
                        QR
                        <IconButton
                            style={{ position: 'absolute', right: 8, top: 8 }}
                            onClick={handleCloseQR}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={{ textAlign: 'center' }}>
                        <QRCode
                            value={record.station_qr}
                            size={256}
                            bgColor="white"
                            fgColor="black"
                            level="H"
                        />
                        <Typography
                            variant="caption"
                            style={{ marginTop: 16, display: 'block' }}
                        >
                            Автозакрытие через {countdown} сек.
                        </Typography>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

const mapStateToProps = state => ({
    countdown: state.virtualStation.countdown,
    isPolling: state.virtualStation.isPolling,
});

const mapDispatchToProps = dispatch => ({
    startPolling: () => dispatch(startPolling()),
    stopPolling: () => dispatch(stopPolling()),
    startCountdown: seconds => dispatch(startCountdown(seconds)),
    stopCountdown: () => dispatch(stopCountdown()),
    setOpenQrScanner: (open) => dispatch(setOpenQrScanner(open)),
    showNotification: (message, type) => dispatch(showNotification(message, type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShowQRButton);
